import React from "react";
import { viewRecentModeAtom } from "../../atoms/modes.atom";
import { useRecoilState } from "recoil";

const ACTIVE_MODE_CLASS = "text-primary bg-info-surface";
const INACTIVE_MODE_CLASS = "text-text-muted bg-transparent";
const MODE_BUTTON_BASE_CLASS =
  "flex flex-1 cursor-pointer items-center justify-center space-x-[6px] rounded border-none py-2 px-[18px] text-sm font-medium focus:outline-none";
const ICON_CLASS = "visible md:invisible lg:visible mr-[6px] fill-current";
const DEFAULT_MODES = [
  { title: "On-Cloud", value: "online" },
  { title: "On-Device", value: "offline" },
];
const SWITCH_ERROR_MESSAGE = "Failed to switch recent title mode";

const CloudIcon = () => (
  <svg
    width="16"
    height="16"
    className={ICON_CLASS}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    id="Cloud"
  >
    <path fill="none" d="M0 0h48v48H0z"></path>
    <path
      d="M38.71 20.07C37.35 13.19 31.28 8 24 8c-5.78 0-10.79 3.28-13.3 8.07C4.69 16.72 0 21.81 0 28c0 6.63 5.37 12 12 12h26c5.52 0 10-4.48 10-10 0-5.28-4.11-9.56-9.29-9.93zM20 34l-7-7 2.83-2.83L20 28.34l10.35-10.35 2.83 2.83L20 34z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
      className="color000000 svgShape"
    ></path>
  </svg>
);

const FolderIcon = () => (
  <svg
    width="16"
    height="16"
    xmlns="http://www.w3.org/2000/svg"
    className={ICON_CLASS}
    viewBox="0 0 512 512"
    id="folder"
  >
    <path
      d="M472 176H40c-4.411 0-8 3.589-8 8v239c0 13.317 11.683 25 25 25h400c12.683 0 23-11.215 23-25V184c0-4.411-3.589-8-8-8zM457 96H215c-2.791 0-4.271-.585-6.094-2.408l-22.501-22.501-.168-.162C181.365 66.333 177.361 64 169 64H57c-13.785 0-25 10.317-25 23v74.376A23.885 23.885 0 0140 160h432c2.805 0 5.496.488 8 1.376V119c0-13.327-9.673-23-23-23z"
      fill="currentColor"
    ></path>
  </svg>
);

const MODE_ICONS = [CloudIcon, FolderIcon];

const getModeButtonClass = (isSelected) =>
  `${MODE_BUTTON_BASE_CLASS} ${
    isSelected ? ACTIVE_MODE_CLASS : INACTIVE_MODE_CLASS
  }`;

const ModeButton = ({ icon: Icon, isSelected, mode, onSelect }) => (
  <button
    type="button"
    aria-pressed={isSelected}
    className={getModeButtonClass(isSelected)}
    onClick={() => onSelect(mode.value)}
  >
    <Icon />
    {mode.title}
  </button>
);

const ModeSeparator = () => (
  <span aria-hidden="true" className="mx-2 h-8 w-px bg-border" />
);

/**
 * @param {{ modes?: Array<{ title: string, value: string }>, onSwitchHandler?: (mode: string) => void }} props
 */
const RecentModeSwitcher = ({
  modes = DEFAULT_MODES,
  onSwitchHandler,
  selectedMode,
}) => {
  const [storedMode, setViewRecentMode] = useRecoilState(viewRecentModeAtom);
  const currentMode = selectedMode ?? storedMode;

  const handleModeSelect = (nextMode) => {
    if (selectedMode === undefined) {
      setViewRecentMode(nextMode);
    }

    Promise.resolve(onSwitchHandler?.(nextMode)).catch((error) => {
      console.error(`${SWITCH_ERROR_MESSAGE}: ${nextMode}`, error);
    });
  };

  const modeButtons = modes.map((mode, index) => {
    const Icon = MODE_ICONS[index] ?? CloudIcon;
    const isSelected = currentMode === mode.value;

    return (
      <React.Fragment key={mode.value}>
        <ModeButton
          icon={Icon}
          isSelected={isSelected}
          mode={mode}
          onSelect={handleModeSelect}
        />
        {index < modes.length - 1 && <ModeSeparator />}
      </React.Fragment>
    );
  });

  return (
    <div className="themeSwitcherTwo shadow-card relative flex w-full select-none items-center rounded-md bg-surface p-1 font-montserrat">
      {modeButtons}
    </div>
  );
};

export default RecentModeSwitcher;
